import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, Alert, Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IconButton, Tooltip } from '@mui/material';
import logoUrl from '../../assets/floodnet_logo_bw_5in.png';

export type WelcomeDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const WelcomeDialogAndButton = ({ storageKey = 'floodnet_welcome_seen_v2' }: { storageKey?: string }) => {
  const { open, setOpen } = useWelcomeDialogState(storageKey);
  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  return (<>
    <Tooltip title="Welcome & tips">
      <IconButton onClick={() => setOpen(true)} aria-label="Welcome and tips" sx={{ padding: 0.5 }}>
        <InfoOutlinedIcon sx={{ fontSize: '0.6em' }} />
      </IconButton>
    </Tooltip>
    <WelcomeDialog open={open} onClose={handleClose} />
  </>)
  
  
};

function useWelcomeDialogState(storageKey = 'floodnet_welcome_seen_v2') {
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    try {
      const seen = localStorage.getItem(storageKey);
      if (!seen) {
        setOpen(true);
        localStorage.setItem(storageKey, '1');
      }
    } catch {
      setOpen(true);
    }
  }, [storageKey]);
  return { open, setOpen };
}

const Tip = ({ bullet, text }: { bullet: string; text: string }) => (
  <Stack direction="row" spacing={1} alignItems="flex-start">
    <Typography component="span" sx={{ fontWeight: 700, minWidth: 20, textAlign: 'center' }}>
      {bullet}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Stack>
);

export function WelcomeDialog({ open, onClose }: WelcomeDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      hideBackdrop={false}
      slotProps={{
        paper: {
          elevation: 6,
          sx: {
            borderRadius: 3,
            p: 2,
            bgcolor: 'rgba(53, 185, 196, 0.21)',
          },
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
        <img src={logoUrl} alt="FloodNet" style={{ height: 96 }} />
        <Box sx={{ lineHeight: 1.1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
            Welcome to
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>
            FloodNet NYC
          </Typography>
          <Button variant="text" size="small" href="https://floodnet.nyc" target="_blank" rel="noopener" sx={{ py: 0, px: 1 }}>
            Project Info, Methodology &amp; Data Sources &gt;
          </Button>
          <Typography variant="caption" sx={{ display: 'block' }}>
            FloodNet is a collaboration between NYC, CUNY, and NYU.
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: 'divider' }}>
        <Stack spacing={1.5}>
          <Typography variant="body1">
            This is an interactive map of real-time and historical flood sensor data across New York City.
          </Typography>
          <Typography variant="body1">
            It visualizes water depth measurements from over 300+ sensors deployed in flood-prone areas to help residents and city officials monitor and respond to flooding risks.
          </Typography>
          <Stack spacing={1}>
            <Tip bullet="🗺️" text="Zoom into the map to filter to specific areas." />
            <Tip bullet="⏱️" text="Use the time scrubber explore the data over time." />
          </Stack>
          <Alert severity="warning">
            Sensors may be offline or report erroneous readings due to snow, trash, or other items placed beneath the sensor. Use your judgment when interpreting the data.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">
          Let's go
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default WelcomeDialog;
